<template>
  <div class="field-charging-point">
    <v-alert v-if="error && !sheet" type="error" dense outlined>{{ error }}</v-alert>
    <div v-if="!sheet && !error" class="text-center py-10"><v-progress-circular indeterminate color="primary" /></div>

    <template v-if="sheet">
      <!-- Juste posée : elle n'est pas finie tant qu'on ne l'a pas vue répondre et accepter un badge. -->
      <v-alert v-if="justCommissioned" type="success" text class="mb-4 text-body-2">
        <div class="font-weight-bold mb-1">{{ $t('onboarding.field.commissioned.title') }}</div>
        <div class="mb-1">{{ $t('onboarding.field.commissioned.check') }}</div>
        <div class="field-charging-point__check">
          <v-icon small :color="sheet.connected ? 'success' : 'error'">{{ sheet.connected ? 'mdi-check-circle' : 'mdi-close-circle' }}</v-icon>
          {{ $t(sheet.connected ? 'onboarding.field.commissioned.connected' : 'onboarding.field.commissioned.not-connected') }}
        </div>
        <div class="field-charging-point__check">
          <v-icon small :color="evsesAvailable ? 'success' : 'warning'">{{ evsesAvailable ? 'mdi-check-circle' : 'mdi-alert-circle' }}</v-icon>
          {{ $t(evsesAvailable ? 'onboarding.field.commissioned.available' : 'onboarding.field.commissioned.not-available', { status: evsesStatus }) }}
        </div>
        <div class="field-charging-point__check">
          <v-icon small color="primary">mdi-play-circle-outline</v-icon>{{ $t('onboarding.field.commissioned.test-charge') }}
        </div>
        <div class="field-charging-point__check">
          <v-icon small color="primary">mdi-card-account-details-outline</v-icon>{{ $t('onboarding.field.commissioned.badge') }}
        </div>
        <div class="d-flex mt-2">
          <v-btn x-small text color="primary" class="px-0 text-none" @click="refresh">
            <v-icon left x-small>mdi-refresh</v-icon>{{ $t('refresh') }}
          </v-btn>
          <v-spacer />
          <v-btn x-small text color="primary" class="px-0 text-none" :to="{ name: 'field' }" exact>{{ $t('onboarding.field.mine') }}</v-btn>
        </div>
      </v-alert>

      <!-- Laquelle, où, et si elle nous parle : l'essentiel en haut. -->
      <v-card class="mb-4">
        <v-card-text class="pa-4">
          <div class="d-flex align-start">
            <div>
              <div class="text-h6 text--primary">{{ sheet.identifier }}</div>
              <div v-if="sheet.name && sheet.name !== sheet.identifier" class="text-body-2">{{ sheet.name }}</div>
            </div>
            <v-spacer />
            <v-chip small :color="sheet.connected ? 'success' : 'grey'" dark>
              {{ $t(sheet.connected ? 'onboarding.field.maintenance.connected' : 'onboarding.field.maintenance.disconnected') }}
            </v-chip>
          </div>
          <div class="mt-3 text-body-1"><v-icon class="mr-1">mdi-map-marker-outline</v-icon>{{ sheet.address || sheet.locationName || $t('onboarding.commissioning.activation.no-location') }}</div>
          <div v-if="sheet.organizationName" class="mt-1 text-body-2 text--secondary"><v-icon small class="mr-1">mdi-domain</v-icon>{{ sheet.organizationName }}</div>
          <div class="mt-3 field-charging-point__facts text-body-2">
            <span class="text--secondary">{{ $t('onboarding.field.maintenance.status') }}</span><span>{{ chargingPointStatusLabel(sheet.status) }}<span v-if="sheet.inMaintenance"> · {{ $t('onboarding.field.maintenance.in-maintenance') }}</span></span>
            <span class="text--secondary">{{ $t('onboarding.field.maintenance.model') }}</span><span>{{ [sheet.vendor, sheet.model].filter(Boolean).join(' ') || '-' }}</span>
            <span class="text--secondary">{{ $t('onboarding.field.maintenance.serial-label') }}</span><span>{{ sheet.serialNumber || '-' }}</span>
            <span class="text--secondary">{{ $t('onboarding.field.maintenance.firmware') }}</span><span>{{ sheet.firmwareVersion || '-' }}</span>
            <span class="text--secondary">{{ $t('onboarding.field.maintenance.last-connection') }}</span><span>{{ sheet.lastConnection ? $options.filters.formatDate(sheet.lastConnection, 'DD/MM/YYYY HH:mm') : '-' }}</span>
          </div>
        </v-card-text>
      </v-card>

      <!-- L'intervention du technicien sur cette borne et son mode test. -->
      <field-intervention
          ref="intervention"
          :charging-point-id="sheet.id"
          :order-id="$route.query.order || null"
          :auto-open="justCommissioned && !!$route.query.order"
          :can-manage="canManage"
          class="mb-4"
          @change="intervention = $event"
      />

      <!-- La réponse de la borne à la dernière action : on l'attend ici, sans quitter la fiche. -->
      <v-alert v-if="sent" :type="sentType" text dense class="mb-4">
        <div class="font-weight-medium">{{ $t(`onboarding.field.maintenance.action.${sent.action}`) }}</div>
        <div>{{ sentText }}</div>
      </v-alert>
      <v-alert v-if="error" type="error" dense text class="mb-4">{{ error }}</v-alert>

      <!-- Les gestes à distance : la borne entière, puis chaque point de charge. -->
      <v-card class="mb-4">
        <v-card-text class="pa-4">
          <div class="field-charging-point__title">{{ $t('onboarding.field.maintenance.actions') }}</div>
          <p v-if="!canManage" class="text-caption text--secondary">{{ $t('onboarding.field.maintenance.no-manage-right') }}</p>
          <div class="field-charging-point__buttons">
            <v-btn x-small outlined height="32" color="primary" :disabled="!canManage || working" @click="confirm('reset', { type: 'Soft' })">{{ $t('onboarding.field.maintenance.action.reset-soft') }}
            </v-btn>
            <v-btn x-small outlined height="32" color="primary" :disabled="!canManage || working" @click="confirm('reset', { type: 'Hard' })">{{ $t('onboarding.field.maintenance.action.reset-hard') }}
            </v-btn>
            <v-btn x-small outlined height="32" color="primary" :disabled="!canManage || working" @click="send('trigger-message', { messageType: 'StatusNotification' })">{{ $t('onboarding.field.maintenance.action.trigger-message') }}
            </v-btn>
            <v-btn x-small outlined height="32" color="primary" :disabled="!canManage || !canConfigure || working" @click="send('get-configuration')">{{ $t('onboarding.field.maintenance.action.get-configuration') }}
            </v-btn>
          </div>

          <div v-for="evse in sheet.evses" :key="evse.externalId" class="field-charging-point__evse">
            <div class="d-flex align-center mb-2">
              <span class="text-body-2 font-weight-medium">{{ evse.evseId || evse.externalId }}</span>
              <v-spacer />
              <v-chip x-small dark :color="evseStatusColor(evse.status)">{{ evseStatusLabel(evse.status) }}</v-chip>
            </div>
            <div class="field-charging-point__buttons field-charging-point__buttons--three">
              <v-btn x-small outlined height="32" color="primary" :disabled="!canManage || working" @click="send('unlock-connector', { evseId: evse.externalId, connectorId: firstConnector(evse) })">{{ $t('onboarding.field.maintenance.evse.unlock') }}
              </v-btn>
              <v-btn x-small outlined height="32" color="primary" :disabled="!canManage || working" @click="send('change-availability', { type: 'operative', connectorId: firstConnector(evse) })">{{ $t('onboarding.field.maintenance.evse.operative') }}
              </v-btn>
              <v-btn x-small outlined height="32" color="primary" :disabled="!canManage || working" @click="confirm('change-availability', { type: 'inoperative', connectorId: firstConnector(evse) })">{{ $t('onboarding.field.maintenance.evse.inoperative') }}
              </v-btn>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <field-test-charge v-if="sheet.evses.length" :charging-point-id="sheet.id" :evses="sheet.evses" :can-manage="canManage" :can-technical="permissions.includes('TECHNICAL_CHARGE')" :intervention="intervention" class="mb-4" />

      <field-sessions :charging-point-id="sheet.id" :refresh-key="refreshKey" class="mb-4" />

      <field-badge-test :charging-point-id="sheet.id" :intervention="intervention" :can-manage="canManage" :can-associate="canAssociate" class="mb-4" @designate="uid => $refs.intervention.designateBadge(uid)" @associated="$refs.intervention.load()" />

      <!-- Ce qu'elle remonte (température, signal…) et sa configuration, depuis la dernière lecture. -->
      <v-card v-if="canConfigure" class="mb-4">
        <v-card-text class="pa-4">
          <div class="d-flex align-center">
            <div class="field-charging-point__title mb-0">{{ $t('onboarding.field.maintenance.configuration') }}</div>
            <v-spacer />
            <v-btn small text color="primary" :loading="configurationLoading" @click="loadConfiguration">{{ $t(configuration ? 'refresh' : 'onboarding.field.maintenance.show') }}</v-btn>
          </div>
          <template v-if="configuration">
            <p class="text-caption text--secondary mt-2 mb-2">
              {{ configuration.readAt ? $t('onboarding.field.maintenance.read-at', { date: $options.filters.formatDate(configuration.readAt, 'DD/MM/YYYY HH:mm') }) : $t('onboarding.field.maintenance.never-read') }}
            </p>
            <div v-for="info in configuration.informations || []" :key="info.code" class="field-charging-point__key">
              <span class="text--secondary">{{ info.label }}</span><span>{{ info.value }}<span v-if="info.unit"> {{ info.unit }}</span></span>
            </div>
            <!-- Les clés, repliées : une centaine de lignes ne doit pas pousser le reste de la fiche hors de l'écran. -->
            <v-expansion-panels v-if="(configuration.keys || []).length" flat class="mt-2 field-charging-point__keys">
              <v-expansion-panel>
                <v-expansion-panel-header class="px-0 py-1">{{ $t('onboarding.field.maintenance.keys', { count: configuration.keys.length }) }}</v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-text-field v-model="keyFilter" dense outlined hide-details clearable class="my-2" :placeholder="$t('onboarding.field.maintenance.key-filter')" />
                  <div v-for="entry in filteredKeys" :key="entry.key" class="field-charging-point__key" :class="{ 'red--text': entry.deviates }">
                    <span class="text--secondary">{{ entry.key }}</span><span>{{ entry.sensitive ? '••••••' : entry.value }}</span>
                  </div>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
          </template>
        </v-card-text>
      </v-card>

      <!-- Ce qu'elle a remonté de travers : le code, le connecteur, quand. -->
      <v-card class="mb-4">
        <v-card-text class="pa-4">
          <div class="field-charging-point__title">{{ $t('onboarding.field.maintenance.errors') }}</div>
          <p v-if="!sheet.errors.length" class="text-body-2 text--secondary mb-0">{{ $t('onboarding.field.maintenance.no-error') }}</p>
          <div v-for="(item, index) in sheet.errors" :key="index" class="field-charging-point__error">
            <div class="text-body-2 font-weight-medium">{{ item.code }}<span v-if="item.vendorCode" class="text--secondary"> · {{ item.vendorCode }}</span></div>
            <div class="text-caption text--secondary">
              {{ item.at ? $options.filters.formatDate(item.at, 'DD/MM/YYYY HH:mm') : '-' }}<span v-if="item.connectorId"> · {{ $t('onboarding.field.maintenance.connector', { connector: item.connectorId }) }}</span><span v-if="item.info"> · {{ item.info }}</span>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </template>

    <!-- Les gestes qui coupent quelqu'un (redémarrer, rendre indisponible) se confirment. -->
    <v-dialog v-model="confirming" max-width="420">
      <v-card v-if="pending">
        <v-card-title class="text-h6">{{ $t(`onboarding.field.maintenance.action.${actionLabel(pending.action, pending.params)}`) }}</v-card-title>
        <v-card-text>{{ $t('onboarding.field.maintenance.confirm-text') }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="confirming = false">{{ $t('cancel') }}</v-btn>
          <v-btn color="primary" depressed @click="confirming = false; send(pending.action, pending.params)">{{ $t('onboarding.field.maintenance.confirm') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FieldRepository from '@repository/FieldRepository'
import CommissioningMixin from '../CommissioningMixin'
import FieldBadgeTest from './FieldBadgeTest.vue'
import FieldIntervention from './FieldIntervention.vue'
import FieldTestCharge from './FieldTestCharge.vue'
import FieldSessions from './FieldSessions.vue'
import { EventBus } from '@includes/EventBus'

/** Ce que la borne pousse au navigateur (notifier) : un de ces messages relit la fiche aussitôt. */
const LIVE_ACTIONS = ['StatusNotification', 'MeterValues', 'BootNotification', 'StartTransaction', 'StopTransaction']
/** Sans notifier (en local, réseau capricieux), la fiche se relit d'elle-même. */
const REFRESH_MS = 10000

/** On attend la réponse de la borne une minute ; au-delà, elle ne répondra plus. */
const ANSWER_TIMEOUT_MS = 60000
const POLL_MS = 2000

/**
 * La fiche de maintenance d'une borne : son état, des gestes à distance (redémarrer, débloquer, rendre disponible,
 * redemander son état, lire sa configuration) dont on attend la réponse sur la fiche, le test de badge, et ses
 * dernières erreurs. Les gestes exigent le droit de piloter une borne ; la configuration, celui de la lire.
 */
export default {
  name: 'FieldChargingPoint',

  components: { FieldBadgeTest, FieldIntervention, FieldTestCharge, FieldSessions },

  mixins: [CommissioningMixin],

  data() {
    return {
      sheet: null,
      intervention: null,
      error: null,
      working: false,
      sent: null,
      confirming: false,
      pending: null,
      configuration: null,
      configurationLoading: false,
      keyFilter: '',
      timer: null,
      refreshKey: 0,
      liveTimer: null,
      liveHandler: null,
      pollTimer: null,
    }
  },

  computed: {
    permissions() {
      return this.currentUser?.permissions || []
    },

    canManage() {
      return this.permissions.includes('STATION_MANAGE')
    },

    canAssociate() {
      return this.permissions.includes('CUSTOMER_WRITE')
    },

    canConfigure() {
      return this.permissions.includes('STATION_CONFIGURATION')
    },

    sentType() {
      return { pending: 'info', answered: this.accepted ? 'success' : 'warning', error: 'error', timeout: 'warning' }[this.sent?.state] || 'info'
    },

    accepted() {
      const status = this.sent?.response?.status
      return !status || ['Accepted', 'Unlocked', 'Scheduled'].includes(status)
    },

    sentText() {
      const s = this.sent
      if (!s) return ''
      if (s.state === 'pending') return this.$t('onboarding.field.maintenance.waiting-answer')
      if (s.state === 'timeout') return this.$t('onboarding.field.maintenance.no-answer')
      if (s.state === 'error') return this.$t('onboarding.field.maintenance.answer-error', { code: s.error?.code || '-' })
      return this.$t('onboarding.field.maintenance.answered', { status: s.response?.status || this.$t('onboarding.field.maintenance.ok') })
    },

    justCommissioned() {
      return this.$route.query.commissioned === '1'
    },

    evsesAvailable() {
      const evses = this.sheet?.evses || []
      return evses.length > 0 && evses.every(evse => evse.status === 'AVAILABLE')
    },

    evsesStatus() {
      return (this.sheet?.evses || []).map(evse => this.evseStatusLabel(evse.status)).join(', ') || '-'
    },

    filteredKeys() {
      const filter = (this.keyFilter || '').toLowerCase()
      return (this.configuration?.keys || []).filter(entry => !filter || entry.key.toLowerCase().includes(filter))
    },
  },

  watch: {
    '$route.params.id'() {
      this.stop()
      this.unlisten()
      Object.assign(this, { sheet: null, sent: null, configuration: null, error: null })
      this.load()
      this.listen()
    },
  },

  created() {
    this.load()
    this.listen()
  },

  beforeDestroy() {
    this.stop()
    this.unlisten()
  },

  methods: {
    async load(attempt = 1) {
      try {
        this.sheet = await FieldRepository.chargingPoint(this.$route.params.id)
        this.error = null
      } catch (e) {
        // Juste posée, sa fiche peut avoir un temps de retard : on réessaie une fois.
        if (attempt === 1 && this.justCommissioned) {
          setTimeout(() => this.load(2), 1500)
          return
        }
        this.error = e?.response ? this.messageOf(e) : this.$t('onboarding.field.offline-list')
      }
    },

    refresh() {
      this.refreshKey++
      return this.load()
    },

    /**
     * Le temps réel : un message de la borne (statut, relevé, démarrage, fin de charge) relit la fiche et ses charges,
     * regroupés sur une seconde ; et toutes les 10 s quand l'écran est visible, au cas où le notifier ne passe pas.
     */
    listen() {
      const id = this.$route.params.id
      this.liveHandler = () => {
        clearTimeout(this.liveTimer)
        this.liveTimer = setTimeout(() => this.refresh(), 1000)
      }
      LIVE_ACTIONS.forEach(action => EventBus.$on(`on-${action}-${id}`, this.liveHandler))
      this.liveId = id
      this.pollTimer = setInterval(() => {
        if (document.visibilityState === 'visible' && this.sheet) {
          this.refresh()
        }
      }, REFRESH_MS)
    },

    unlisten() {
      if (this.liveHandler) {
        LIVE_ACTIONS.forEach(action => EventBus.$off(`on-${action}-${this.liveId}`, this.liveHandler))
      }
      clearTimeout(this.liveTimer)
      clearInterval(this.pollTimer)
      this.liveHandler = null
    },

    confirm(action, params) {
      this.pending = { action, params }
      this.confirming = true
    },

    /** Envoyer, puis attendre la réponse de la borne sur la fiche. */
    async send(action, params = {}) {
      this.stop()
      this.working = true
      this.error = null
      const label = this.actionLabel(action, params)
      try {
        const result = await FieldRepository.sendAction(this.sheet.id, action, params)
        this.sent = { action: label, state: 'pending', messageId: result.messageIds[0], startedAt: Date.now(), configuration: action === 'get-configuration' }
        this.poll()
      } catch (e) {
        this.sent = null
        this.error = e?.response ? (e.response.data?.reason || this.messageOf(e)) : this.$t('onboarding.field.offline-action')
      } finally {
        this.working = false
      }
    },

    poll() {
      this.timer = setTimeout(async () => {
        if (!this.sent || this.sent.state !== 'pending') return
        if (Date.now() - this.sent.startedAt > ANSWER_TIMEOUT_MS) {
          this.sent = { ...this.sent, state: 'timeout' }
          return
        }
        try {
          const outcome = await FieldRepository.actionOutcome(this.sheet.id, this.sent.messageId)
          this.sent = { ...this.sent, ...outcome }
          if (outcome.state !== 'pending') {
            this.afterAnswer()
            return
          }
        } catch (e) {
          // Réseau coupé un instant : on réessaie au tour suivant.
        }
        this.poll()
      }, POLL_MS)
    },

    /** Une lecture de configuration répondue : on la montre ; un état redemandé : la fiche se recharge. */
    afterAnswer() {
      if (this.sent.configuration) {
        this.loadConfiguration()
      }
      this.load()
    },

    stop() {
      clearTimeout(this.timer)
      this.timer = null
    },

    async loadConfiguration() {
      this.configurationLoading = true
      try {
        this.configuration = await FieldRepository.configuration(this.sheet.id)
      } catch (e) {
        this.error = e?.response ? this.messageOf(e) : this.$t('onboarding.field.offline-list')
      } finally {
        this.configurationLoading = false
      }
    },

    actionLabel(action, params = {}) {
      if (action === 'reset') return params.type === 'Hard' ? 'reset-hard' : 'reset-soft'
      if (action === 'change-availability') return params.type === 'inoperative' ? 'inoperative' : 'operative'
      return action
    },

    firstConnector(evse) {
      return (evse.connectors || [])[0]?.connectorId || 0
    },

    /** L'état d'un point de charge en couleur : on voit d'un coup d'œil qu'il passe en charge ou en indisponible. */
    evseStatusColor(status) {
      return {
        AVAILABLE: 'success', CHARGING: 'blue', RESERVED: 'indigo', BLOCKED: 'orange',
        INOPERATIVE: 'grey darken-1', MAINTENANCE: 'grey darken-1', OUTOFORDER: 'error',
      }[status] || 'grey'
    },

        evseStatusLabel(status) {
      if (!status) return '-'
      const key = `enums.evse-status.${status}`
      return this.$te(key) ? this.$t(key) : status
    },
  },
}
</script>

<style scoped>
.field-charging-point__title {
  font-weight: 500;
  font-size: 1.05rem;
  margin-bottom: 12px;
}
.field-charging-point__buttons {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}
.field-charging-point__buttons >>> .v-btn {
  width: 100%;
  min-width: 0 !important;
  padding: 0 8px !important;
  font-size: 0.75rem;
  letter-spacing: 0;
  text-transform: none;
}
.field-charging-point__buttons >>> .v-btn__content {
  justify-content: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.field-charging-point__buttons--three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.field-charging-point__evse {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.field-charging-point__facts {
  display: grid;
  grid-template-columns: auto 1fr;
  column-gap: 12px;
  row-gap: 2px;
}
.field-charging-point__key {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  font-size: 0.85rem;
  padding: 4px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  word-break: break-all;
}
.field-charging-point__check {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
}
.field-charging-point__error {
  padding: 6px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.field-charging-point__keys >>> .v-expansion-panel-content__wrap {
  padding: 0;
}
</style>
